import { __assign, __read, __values } from "tslib";
import { FilterBy, HarmonyEntity, } from '@office-start/command-types';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UnreactiveHarmonyCommandStore } from './store';
/**
 * @returns Command filter text when messages from the queue are picked up
 * @todo Support clearing the filter on user interaction
 * @todo These filters are rudimentary and not the final implementation. These would be
 * swapped in with ALL MC 3.0 filters (and not just those for text as are below)
 */
export var useCommandFilter = function () {
    var _a;
    var _b = __read(useState(undefined), 2), filterText = _b[0], setFilterText = _b[1];
    var _c = __read(useState(undefined), 2), sharedByFilterText = _c[0], setSharedByFilterText = _c[1];
    var _d = __read(useState(undefined), 2), tagText = _d[0], setTagText = _d[1];
    var _e = __read(useState(undefined), 2), fileTypeText = _e[0], setFileTypeText = _e[1];
    var contentEntity = UnreactiveHarmonyCommandStore.getEntity(HarmonyEntity.Content);
    var contentFilters = contentEntity.filter;
    var currentActiveFilterModels = useMemo(function () {
        return contentEntity.activeFilterModels;
    }, [contentEntity.activeFilterModels]);
    var data = useFeatureGates().data;
    var isCommandingEnabled = useMemo(function () {
        var _a;
        return (_a = data === null || data === void 0 ? void 0 : data.result.chatGates) === null || _a === void 0 ? void 0 : _a.chatEnabled;
    }, [(_a = data === null || data === void 0 ? void 0 : data.result.chatGates) === null || _a === void 0 ? void 0 : _a.chatEnabled]);
    useEffect(function () {
        var e_1, _a;
        if (!isCommandingEnabled) {
            return;
        }
        if ((!contentFilters || contentFilters.length === 0) &&
            filterText &&
            sharedByFilterText &&
            tagText) {
            if (filterText) {
                setFilterText(undefined);
            }
            if (sharedByFilterText) {
                setSharedByFilterText(undefined);
            }
            if (tagText) {
                setTagText(undefined);
            }
        }
        if (contentFilters === undefined) {
            return;
        }
        try {
            // Directly set the filter and shared by text. $TODO: update/use the filter store.
            for (var contentFilters_1 = __values(contentFilters), contentFilters_1_1 = contentFilters_1.next(); !contentFilters_1_1.done; contentFilters_1_1 = contentFilters_1.next()) {
                var contentFilter = contentFilters_1_1.value;
                if (contentFilter.by === FilterBy.Name) {
                    setFilterText(contentFilter.name);
                }
                if (contentFilter.by === FilterBy.SharedBy) {
                    setSharedByFilterText(contentFilter.name);
                }
                if (contentFilter.by === FilterBy.Tag) {
                    setTagText(contentFilter.name);
                }
                if (contentFilter.by === FilterBy.FileType) {
                    setFileTypeText(contentFilter.fileType);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (contentFilters_1_1 && !contentFilters_1_1.done && (_a = contentFilters_1.return)) _a.call(contentFilters_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }, [
        isCommandingEnabled,
        contentFilters,
        filterText,
        sharedByFilterText,
        tagText,
        currentActiveFilterModels,
    ]);
    var updateEntity = useCallback(function (entity) {
        UnreactiveHarmonyCommandStore.updateEntity(entity);
    }, []);
    var clearCommandFilterNameText = useCallback(function () {
        updateEntity({
            filter: contentFilters === null || contentFilters === void 0 ? void 0 : contentFilters.filter(function (filter) { return filter.by !== FilterBy.SharedBy; }),
            entity: HarmonyEntity.Content,
        });
        setSharedByFilterText(undefined);
    }, [contentFilters, updateEntity]);
    var clearCommandFilterByName = useCallback(function () {
        updateEntity({
            filter: contentFilters === null || contentFilters === void 0 ? void 0 : contentFilters.filter(function (filter) { return filter.by !== FilterBy.Name; }),
            entity: HarmonyEntity.Content,
        });
        setFilterText(undefined);
    }, [contentFilters, updateEntity]);
    var clearCommandFilterFileText = useCallback(function () {
        updateEntity({
            filter: contentFilters === null || contentFilters === void 0 ? void 0 : contentFilters.filter(function (filter) { return filter.by !== FilterBy.FileType; }),
            entity: HarmonyEntity.Content,
        });
        setFileTypeText(undefined);
    }, [contentFilters, updateEntity]);
    var clearCommandFilterTagText = useCallback(function () {
        updateEntity({
            filter: contentFilters === null || contentFilters === void 0 ? void 0 : contentFilters.filter(function (filter) { return filter.by !== FilterBy.Tag; }),
            entity: HarmonyEntity.Content,
        });
        setTagText(undefined);
    }, [contentFilters, updateEntity]);
    var updateActiveCommandFilterModels = useCallback(function (selectedFilter, isSelected, clearAll) {
        if (clearAll) {
            updateEntity(__assign(__assign({}, contentEntity), { activeFilterModels: [], filter: undefined }));
            return;
        }
        var activeFilters = currentActiveFilterModels;
        if (isSelected) {
            //Only one date filter can exist at a time
            activeFilters = activeFilters === null || activeFilters === void 0 ? void 0 : activeFilters.filter(function (filter) {
                return filter.filterModel.filterType.toString() &&
                    (selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.filterModel.filterType.toString()) !==
                        'Date';
            });
            selectedFilter &&
                activeFilters &&
                activeFilters.push(selectedFilter);
        }
        else {
            activeFilters =
                activeFilters &&
                    activeFilters.filter(function (filter) {
                        return filter.filterModel.filterId !==
                            (selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.filterModel.filterId);
                    });
        }
        updateEntity(__assign(__assign({}, contentEntity), { activeFilterModels: activeFilters, filter: undefined }));
    }, [contentEntity, currentActiveFilterModels, updateEntity]);
    return useMemo(function () {
        return {
            fileTypeText: fileTypeText,
            filterText: filterText,
            sharedByFilterText: sharedByFilterText,
            tagText: tagText,
            clearCommandFilterByName: clearCommandFilterByName,
            clearCommandFilterNameText: clearCommandFilterNameText,
            clearCommandFilterFileText: clearCommandFilterFileText,
            clearCommandFilterTagText: clearCommandFilterTagText,
            currentActiveFilterModels: currentActiveFilterModels,
            updateActiveCommandFilterModels: updateActiveCommandFilterModels,
        };
    }, [
        fileTypeText,
        filterText,
        sharedByFilterText,
        tagText,
        clearCommandFilterByName,
        clearCommandFilterNameText,
        clearCommandFilterFileText,
        clearCommandFilterTagText,
        currentActiveFilterModels,
        updateActiveCommandFilterModels,
    ]);
};
//# sourceMappingURL=useCommandFilter.js.map